import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Per-user watermark of the informational items already shown in the bell
 * (« officiel rattaché à une autre ligue »), keyed by user id. It is the newest
 * `added_at` (server time) loaded when the bell was opened — never the client
 * clock, so a skewed clock cannot hide a later notice. Those items count in
 * the badge only while `added_at > watermark`; actionable items always count.
 */
interface NotificationSeenState {
  officialLinksSeenAt: Record<string, string>;
  /** Moves the user's watermark forward to `at`; never backwards. */
  advanceOfficialLinksSeen: (userId: string, at: string) => void;
}

export const useNotificationSeenStore = create<NotificationSeenState>()(
  persist(
    (set) => ({
      officialLinksSeenAt: {},
      advanceOfficialLinksSeen: (userId, at) =>
        set((state) => {
          const current = state.officialLinksSeenAt[userId];
          if (current && new Date(at).getTime() <= new Date(current).getTime()) return state;
          return { officialLinksSeenAt: { ...state.officialLinksSeenAt, [userId]: at } };
        }),
    }),
    {
      name: 'notification-seen-at', // localStorage key
    }
  )
);

/** Newest timestamp of a list (ISO strings), or undefined when empty. */
export const latestTimestamp = (values: string[]): string | undefined =>
  values.reduce<string | undefined>(
    (max, v) => (!max || new Date(v).getTime() > new Date(max).getTime() ? v : max),
    undefined
  );

/** An informational item is unread while it is newer than the watermark (none yet = unread). */
export const isNewerThanSeen = (createdAt: string, seenAt: string | null | undefined): boolean =>
  !seenAt || new Date(createdAt).getTime() > new Date(seenAt).getTime();
